import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { ToggleSwitch } from '@arctheme-components/elements/ToggleSwitch/ToggleSwitch';
import { useAlpineStore } from '@arctheme-hooks/useAlpineStore';
import { useSearch } from '@src/contexts/SearchProvider';
import { useCollection } from '@src/contexts/CollectionContext';
import { getReadableFilterValue, getDisplayLabelForStoredValue, getFilterValueForStorage } from '@src/utils/filterUtils';
import { useSearchContext } from '@src/contexts/SearchContext';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import MultiRangeSlider from '@src/stories/elements/MultirangeSlider/MultiRangeSlider';

// Debounce utility function
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

export const CollectionToolbar = ({
  settings,
  toggleSidebar,
  filterButtonSettings,
  sortDropdownSettings,
  toolbarLayout = 'default',
  productCountPosition = 'center',
  showFilterIcon = true,
  showSortLabel = false,
  showActiveFiltersInToolbar = true,
  totalResults,
  filterSortDirection = 'normal',
  filters = [], // Add filters prop to get filter metadata
  showPriceSlider = false, // Add option to show price slider
  isSearchToolbar = false, // Add prop to distinguish search toolbar
  resultsTextSettings, // NEW: results text block settings
  type = 'plp',
  query = ''
}) => {
  const search = useSearch();
  const { isMobile } = useResponsiveBreakpoints();
  
  // State for mobile filter panel
  const [showMobileFilterPanel, setShowMobileFilterPanel] = useState(false);
  // State to control which filter accordion is open in mobile panel
  const [mobileOpenKey, setMobileOpenKey] = useState<string | null>(null);
  
  // Helper to emit mobile filter open/close events for other components (e.g., sticky header)
  const emitMobileFilterToggle = (open: boolean) => {
    try {
      window.dispatchEvent(new CustomEvent('mobile-filter-toggle', { detail: { open } }));
    } catch (e) {
      // No-op on dispatch errors
    }
  };

  const openMobileFilterPanel = () => {
    emitMobileFilterToggle(true);
    setShowMobileFilterPanel(true);
  };

  const closeMobileFilterPanel = () => {
    emitMobileFilterToggle(false);
    setShowMobileFilterPanel(false);
  };

  // State for tracking which individual filter is open (desktop)
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  // Price range state for individual filters
  const [priceRanges, setPriceRanges] = useState({});
  const [originalPriceBounds, setOriginalPriceBounds] = useState({});

  // Memoized price range setter to prevent infinite re-renders
  const handleSetPriceRange = useCallback((filterKey, range) => {
    setPriceRanges(prev => ({ ...prev, [filterKey]: range }));
  }, []);

  const showMobileDivider = true;

  // Utility function to extract clean filter value for URLs
  const extractFilterValue = (option) => {
    let parsed = JSON.parse(option.input);
    
    // Handle double-encoded JSON
    if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('"{'))) {
        parsed = JSON.parse(parsed);
    }

    const inner = Object.values(parsed)[0];
    let filterValue;
    if (inner && typeof inner === 'object' && (('id' in inner) || ('value' in inner))) {
      filterValue = (inner as any).id || (inner as any).value;
    } else {
      filterValue = inner;
    }

    return filterValue;
  };

  // Add styles for price slider
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .slider-thumb-primary::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #000;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      }
      .slider-thumb-primary::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #000;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        -moz-appearance: none;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Store original price bounds when filters are first loaded - EXACT COPY FROM COLLECTIONSIDEBAR
  useEffect(() => {
    if (filters && filters.length > 0) {
      const newOriginalBounds = {};
      
      filters.forEach((filter: any) => {
        // Check if this is a price filter
        if (
          filter.key === 'Price' ||
          filter.key === 'price' ||
          filter.type === 'PRICE' ||
          filter.type === 'PRICE_RANGE' ||
          filter.key === 'filter.v.price' ||
          filter.key?.includes('price') ||
          filter.label?.toLowerCase().includes('price')
        ) {
          // Extract price values to determine original bounds
          const priceValues = filter.values
            .map((value) => {
              if (value.input) {
                // Handle string input that needs parsing
                if (typeof value.input === 'string') {
                  try {
                    const parsedInput = JSON.parse(value.input);
                    if (parsedInput.price) {
                      return { min: parsedInput.price.min, max: parsedInput.price.max };
                    }
                  } catch (e) {
                    // Silent fallback
                  }
                }
                // Handle already parsed object
                else if (typeof value.input === 'object' && value.input.price) {
                  return { min: value.input.price.min, max: value.input.price.max };
                }
              }

              // Fallback to parsing the key field
              try {
                const parsed = JSON.parse(value.input);
                if (parsed.price) {
                  return { min: parsed.price.min, max: parsed.price.max };
                }
              } catch (e) {
                // Handle non-JSON price values
                const parts = value.input.split('-');
                if (parts.length === 2) {
                  const minPrice = parseFloat(parts[0]);
                  const maxPrice = parseFloat(parts[1]);
                  if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                    return { min: minPrice, max: maxPrice };
                  }
                }

                // Try to extract from label or other formats
                if (value.label && value.label !== 'Price') {
                  const labelParts = value.label.split('-');
                  if (labelParts.length === 2) {
                    const minPrice = parseFloat(labelParts[0].replace(/[^0-9.]/g, ''));
                    const maxPrice = parseFloat(labelParts[1].replace(/[^0-9.]/g, ''));
                    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                      return { min: minPrice, max: maxPrice };
                    }
                  }
                }
              }

              return null;
            })
            .filter(Boolean);

          if (priceValues.length > 0) {
            // Only set original bounds if not already set (preserve the first unfiltered data)
            if (!originalPriceBounds[filter.key]) {
              newOriginalBounds[filter.key] = {
                min: Math.min(...priceValues.map((p) => p.min)),
                max: Math.max(...priceValues.map((p) => p.max))
              };
            }
          }
        }
      });

      // Only update if we have new bounds to set
      if (Object.keys(newOriginalBounds).length > 0) {
        setOriginalPriceBounds(prev => ({ ...prev, ...newOriginalBounds }));
      }
    }
  }, [filters]); // Only depend on filters, not activeFilters

  let context;
  try {
    context = useSearchContext();
  } catch (e) {
    context = useCollection();
  }
  const { sortOption, changeSortOption, loading, activeFilters, applyFilters } = context;
  // Default labels
  const defaultFilterLabel = 'Filters';
  const defaultSortLabel = 'Sort By:';

  // Use settings labels if available, otherwise use defaults
  const filterLabel = filterButtonSettings?.filter_button_label || defaultFilterLabel;
  const sortLabel = sortDropdownSettings?.sort_label || defaultSortLabel;

  // Icon settings from blocks
  const showFilterIconFromBlock = filterButtonSettings?.show_icon !== false;
  const filterIconClass = filterButtonSettings?.icon_class || 'icon-filter-simple';
  const filterIconPosition = filterButtonSettings?.icon_position || 'left';
  const filterIconSize = filterButtonSettings?.icon_size || 15;

  const showSortIcon = sortDropdownSettings?.show_sort_icon || false;
  const sortIconClass = sortDropdownSettings?.sort_icon_class || 'icon-sort';
  const sortIconPosition = sortDropdownSettings?.sort_icon_position || 'left';

  // Determine final show icon state (global setting AND block setting)
  const finalShowFilterIcon = showFilterIcon && showFilterIconFromBlock;

  // Determine flex direction based on filterSortDirection
  const flexDirection = filterSortDirection === 'reverse' ? 'flex-row-reverse' : 'flex-row';

  // Construct class names based on settings
  const filterButtonClasses = isMobile
    ? filterButtonSettings?.filter_button_text_style_mobile
    : filterButtonSettings?.filter_button_text_style_desktop;
  const sortLabelClasses = isMobile
    ? sortDropdownSettings?.sort_label_text_style_mobile
    : sortDropdownSettings?.sort_label_text_style_desktop;
  const sortSelectClasses = isMobile
    ? sortDropdownSettings?.sort_options_text_style_mobile
    : sortDropdownSettings?.sort_options_text_style_desktop;

  // Define sort options based on toolbar type
  let sortOptions;
  if (isSearchToolbar) {
    sortOptions = [
      { value: 'Relevance', label: 'Relevance' },
      { value: 'LowToHigh', label: 'Price - Low to High' },
      { value: 'HighToLow', label: 'Price - High to Low' }
    ];
  } else {
    sortOptions = [
      { value: 'Popular', label: 'Best Selling' },
      { value: 'TitleAZ', label: 'Alphabetically, A-Z' },
      { value: 'TitleZA', label: 'Alphabetically, Z-A' },
      { value: 'Oldest', label: 'Date, old to new' },
      { value: 'Newest', label: 'Date, new to old' },
      { value: 'LowToHigh', label: 'Price - Low to High' },
      { value: 'HighToLow', label: 'Price - High to Low' },
      { value: 'Featured', label: 'Featured' }
    ];
  }

  // Handle sort selection
  const handleSelectChange = (event) => {
    const newValue = event.target.value;
    changeSortOption(newValue, isSearchToolbar ? 'search' : 'plp');
  };

  // Optional: In-stock toggle handler
  const handleToggleChange = (isChecked) => {
    // This could be integrated with the CollectionContext applyFilters method
    const filterEvent = new CustomEvent('searchrequest-filter-applied', {
      detail: {
        filters_object: isChecked ? { available: ['true'] } : {},
        type: isSearchToolbar ? 'search' : 'plp'
      }
    });
    window.dispatchEvent(filterEvent);
  };

  // Handle individual filter removal
  const handleFilterRemoval = (filterKey, value) => {
    const newFilters = { ...activeFilters };
    if (newFilters[filterKey]) {
      newFilters[filterKey] = newFilters[filterKey].filter((v) => v !== value);
      if (newFilters[filterKey].length === 0) {
        delete newFilters[filterKey];
      }
    }
    applyFilters(newFilters, isSearchToolbar ? 'search' : 'plp');
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    applyFilters({}, isSearchToolbar ? 'search' : 'plp');
  };

  let activeFilterCount = 0;
  if (activeFilters) {
    // Calculate active filter count for badge
    activeFilterCount = Object.keys(activeFilters).reduce((count, key) => {
      return count + (activeFilters[key]?.length || 0);
    }, 0);
  }

  // Component for displaying active filters
  const ActiveFiltersBar = () => {
    if (!showActiveFiltersInToolbar || activeFilterCount === 0 || filters.length === 0) return null;

    return (
      <div className='active-filters-bar mt-[10px] px-[14px] md:px-5 mb-5'>
          <div className='flex items-center space-x-3'>
            <div className='flex items-center gap-[10px] flex-wrap'>
              {Object.entries(activeFilters).map(([filterKey, values]) =>
                (Array.isArray(values) ? values : []).map((value, index) => {
                  // Use the new function to get proper display labels for stored GID values
                  const displayLabel = getDisplayLabelForStoredValue(filterKey, value, filters);
                  
                  // Hide the Availability active-filter tag when it shows the default boolean value (true/false)
                  const shouldHideTag = (() => {
                    const key = (filterKey || '').toString().toLowerCase();
                    if (!key.includes('availability')) return false;
                    const val = (displayLabel ?? value ?? '').toString().trim().toLowerCase();
                    return val === 'true' || val === 'false' || val === '1' || val === '0';
                  })();
                  
                  if (shouldHideTag) return null;
                  
                  return (
                    <span
                      key={`${filterKey}-${value}-${index}`}
                      className='active-filter-item text-[13px] cursor-pointer group'
                      onClick={() => handleFilterRemoval(filterKey, value)}
                    >
                      {displayLabel}
                      <button className='ml-2 text-black transition-colors duration-200 group-hover:scale-110'>
                        <i className='icon icon-close text-[10px]'></i>
                      </button>
                    </span>
                  );
                })
              )}
              {/* <button
                className='b7--medium'
                onClick={handleClearAllFilters}
              >
                Clear all
              </button> */}
            </div>
          </div>
      </div>
    );
  };

  // Filter Button Component
  const FilterButton = () => {
    const buttonContent = () => {
      const iconElement = finalShowFilterIcon && (
        <div className='relative flex'>
          <i
            className={`icon ${filterIconClass}`}
            style={{ fontSize: `${filterIconSize}px` }}
          ></i>
          {activeFilterCount > 0 && (
            <span className='absolute flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full -top-2 -right-2 bg-primary filter-badge'>
              {activeFilterCount}
            </span>
          )}
        </div>
      );

      const textElement = <span>{filterLabel}</span>;

      if (filterIconPosition === 'right') {
        return (
          <>
            {textElement}
            {iconElement}
          </>
        );
      } else {
        return (
          <>
            {iconElement}
            {textElement}
          </>
        );
      }
    };

    return (
      <div className='flex items-center'>
        <button
          className={`${filterButtonClasses} flex items-center relative group duration-200 ${flexDirection} ${
            finalShowFilterIcon ? 'space-x-2' : ''
          }`}
          onClick={toggleSidebar}
        >
          {buttonContent()}
        </button>
      </div>
    );
  };

  // Product Count Component
  const ProductCount = () => {
    if (productCountPosition === 'hidden' || (totalResults === 0 && loading)) return null;
    
    // Add state to control delayed showing of "no results" message
    const [showNoResults, setShowNoResults] = useState(false);
    
    // Delay showing "no results" to prevent brief flash on page load
    useEffect(() => {
      if (totalResults === 0 && !loading) {
        const timer = setTimeout(() => {
          setShowNoResults(true);
        }, 500); // 500ms delay
        
        return () => clearTimeout(timer);
      } else {
        setShowNoResults(false);
      }
    }, [totalResults, loading]);
    
    // Don't render anything if totalResults is 0 but we haven't shown the delay yet
    if (totalResults === 0 && !showNoResults) return null;
    
    const resultsFontClass = isMobile
      ? resultsTextSettings?.text_style_mobile
      : resultsTextSettings?.text_style_desktop;

    // Determine if this is a search results page or collection page
    const isSearchResults = isSearchToolbar || type === 'search' || (query && query.trim() !== '');
    
    // Render different text based on context
    const renderResultsText = () => {
      if (totalResults === 0 && showNoResults) {
        if (isSearchResults && query) {
          return (
            <>
              No results for <span className="font-bold">"{query}"</span>
            </>
          );
        }
        return 'No products found';
      }

      if (isSearchResults && query) {
        // Search results page - show "X results for 'query'"
        return (
          <>
            {totalResults} {totalResults === 1 ? 'Search Result' : 'Search Results'} for <span className="font-bold">{query}</span>
          </>
        );
      } else {
        // Collection page - show "X products"
        return `${totalResults} ${totalResults === 1 ? 'product' : 'products'}`;
      }
    };

    return (
      <span className={`results-text text-[#292C2D] ${isMobile ? 'l3' : 'b3'} ${resultsFontClass} ${totalResults > 0 && loading ? 'opacity-0' : ''}`}>
        {renderResultsText()}
      </span>
    );
  };

  // Custom Sort Dropdown Component
  const SortDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle option selection
    const handleOptionSelect = (value) => {
      changeSortOption(value, isSearchToolbar ? 'search' : 'plp');
      setIsOpen(false);
    };

    // Get selected option
    const selectedOption = sortOptions.find((option) => option.value.toLowerCase() === sortOption.toLowerCase());

    return (
      <div
        className='relative w-full custom-sort-dropdown'
        ref={dropdownRef}
      >
        {/* Dropdown trigger */}
        <button
          className={`flex items-center cursor-pointer ${sortLabelClasses} hover:opacity-75 transition-opacity ${isMobile ? 'w-full gap-2' : 'space-x-5'}`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={loading}
        >
          <span>{sortLabel}</span>
          {/* Desktop: Show selected value */}
          {!isMobile && selectedOption && <span>{selectedOption.label}</span>}
          {/* Icon */}
          {showSortIcon &&
            <i
              className={`icon ${isOpen ? `${sortIconClass}` : `${sortIconClass}`} text-[20px] transition-all duration-300 transform ${
                isOpen ? 'rotate-180 opacity-75' : 'rotate-0 opacity-100'
              }`}
            />
          }
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className='absolute right-0 z-50 py-3 pl-4 pr-4 mt-1 space-y-3 overflow-y-auto bg-white border rounded shadow-lg top-full'>
            {sortOptions.map((option) => (
              <button
                key={option.value}
                className={`w-full justify-end whitespace-nowrap ${sortSelectClasses}`}
                onClick={() => handleOptionSelect(option.value)}
              >
                <span
                  style={{
                    fontWeight: option.value.toLowerCase() === sortOption.toLowerCase() ? 'bold' : 'normal'
                  }}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Individual Filter Dropdown Component
  const IndividualFilterDropdown = ({ filter, filterKey, loading }: { filter: any, filterKey: string, loading: boolean }) => {
    // Get active count for this filter
    const activeCount = activeFilters[filterKey]?.length || 0;
    const isOpen = openFilterKey === filterKey;

    // Handle filter button click
    const handleFilterClick = () => {
      // Toggle open state for this filter
      if (isOpen) {
        setOpenFilterKey(null); // Close if already open
      } else {
        setOpenFilterKey(filterKey); // Open this filter
      }
    };
    return (
      <div className='individual-filter-dropdown'>
        {/* Dropdown trigger */}
        <button
          className={`flex items-center space-x-2 md cursor-pointer`}
          onClick={handleFilterClick}
          disabled={loading}
        >
          <span className={`b4--bold ${activeCount > 0 ? 'font-medium text-primary' : 'text-gray-700'}`}>
            {filter.label}
          </span>
          <i
            className={`icon ${isOpen ? 'icon-minus' : 'icon-plus'} text-[20px] transition-all duration-300 transform ${
              isOpen ? 'rotate-180 opacity-75' : 'rotate-0 opacity-100'
            }`}
          />
        </button>
      </div>
    );
  };

  // Mobile Filter Panel Component
  const MobileFilterPanel = () => {
    if (!showMobileFilterPanel) return null;

    // Prevent background scrolling while panel is open
    useEffect(() => {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow || '';
      };
    }, []);

    // Handle filter option selection for mobile - matches desktop logic
    const handleMobileFilterSelect = (filter, option) => {
      const filterValue = extractFilterValue(option);
      const newFilters = { ...activeFilters };
      if (!newFilters[filter.key]) newFilters[filter.key] = [];

      if (newFilters[filter.key].includes(filterValue)) {
        newFilters[filter.key] = newFilters[filter.key].filter(v => v !== filterValue);
        if (newFilters[filter.key].length === 0) delete newFilters[filter.key];
      } else {
        newFilters[filter.key].push(filterValue);
      }

      applyFilters(newFilters, isSearchToolbar ? 'search' : 'plp');
    };

    // Handle price range changes for mobile
    const handleMobilePriceChange = (filter, minPrice, maxPrice) => {
      const newFilters = { ...activeFilters };

      if (minPrice !== null || maxPrice !== null) {
        let priceValue = '';
        if (minPrice !== null && maxPrice !== null) priceValue = `${minPrice}-${maxPrice}`;
        else if (minPrice !== null) priceValue = `${minPrice}`;
        else if (maxPrice !== null) priceValue = `-${maxPrice}`;

        newFilters[filter.key] = [priceValue];
      } else {
        delete newFilters[filter.key];
      }

      applyFilters(newFilters, isSearchToolbar ? 'search' : 'plp');
    };

    return (
      <>
        {/* Backdrop */}
        <div
          className='fixed inset-0 bg-black bg-opacity-50 z-[1001]'
          onClick={() => closeMobileFilterPanel()}
        />

        {/* Panel - Full screen overlay, sits above header */}
        <div className='fixed flex flex-col top-0 left-0 h-full w-full bg-white z-[1002] transform transition-transform duration-300 overflow-y-auto'>
          <div className="flex h-full">
            <div className="w-full px-6 pt-8">
              <div className='sticky top-0 bg-white z-[1003] mb-8'>
                  <div className='flex items-center justify-between'>
                    <h3 className='h3'>Filter by</h3>
                  </div>
                  <ActiveFiltersBar />
              </div>

              <div className='flex flex-col space-y-8'>
                {filters.map((filter: any, index: number) => {
                  const filterKey = filter.key;
                  const activeCount = activeFilters[filterKey]?.length || 0;

                  const isPriceFilter = (
                    filter.key === 'Price' || filter.key === 'price' || filter.type === 'PRICE' || filter.type === 'PRICE_RANGE' || filter.key === 'filter.v.price' || filter.key?.includes('price') || filter.label?.toLowerCase().includes('price')
                  );

                  const isOpen = mobileOpenKey === filterKey;

                  return (
                    <div key={index} className='pb-4'>
                      {/* Accordion header */}
                      <div className='flex items-center space-x-3 cursor-pointer' onClick={() => setMobileOpenKey(isOpen ? null : filterKey)}>
                        <i className={`icon ${isOpen ? 'icon-minus' : 'icon-plus'} text-[20px] transition-transform`} />
                        <div className='flex items-center space-x-3'>
                          <span className='h3'>{filter.label || filter.display_name || filter.filter_name}</span>
                          {/* {activeCount > 0 && (
                            <span className='flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full bg-primary'>
                              {activeCount}
                            </span>
                          )} */}
                        </div>
                      </div>

                      {/* Accordion content */}
                      {isOpen && (
                        <div className={`mt-4 grid gap-4 grid-cols-1 px-[60px]`}>
                          {isPriceFilter ? (
                            // Advanced Price Range UI with MultiRangeSlider - EXACT COPY FROM DESKTOP
                            (() => {
                              // Extract min and max prices from filter values - memoized to prevent infinite re-renders
                              const priceValues = useMemo(
                                () =>
                                  filter.values
                                    .map((value) => {
                                      // First try to use the input field (already parsed object)
                                      if (value.input) {
                                        // Handle string input that needs parsing
                                        if (typeof value.input === 'string') {
                                          try {
                                            const parsedInput = JSON.parse(value.input);
                                            if (parsedInput.price) {
                                              return { min: parsedInput.price.min, max: parsedInput.price.max };
                                            }
                                          } catch (e) {
                                            // Silent fallback
                                          }
                                        }
                                        // Handle already parsed object
                                        else if (typeof value.input === 'object' && value.input.price) {
                                          return { min: value.input.price.min, max: value.input.price.max };
                                        }
                                      }

                                      // Fallback to parsing the key field
                                      try {
                                        const parsed = JSON.parse(value.input);
                                        if (parsed.price) {
                                          return { min: parsed.price.min, max: parsed.price.max };
                                        }
                                      } catch (e) {
                                        // Handle non-JSON price values
                                        const parts = value.input.split('-');
                                        if (parts.length === 2) {
                                          const minPrice = parseFloat(parts[0]);
                                          const maxPrice = parseFloat(parts[1]);
                                          if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                                            return { min: minPrice, max: maxPrice };
                                          }
                                        }

                                        // Try to extract from label or other formats
                                        if (value.label && value.label !== 'Price') {
                                          const labelParts = value.label.split('-');
                                          if (labelParts.length === 2) {
                                            const minPrice = parseFloat(labelParts[0].replace(/[^0-9.]/g, ''));
                                            const maxPrice = parseFloat(labelParts[1].replace(/[^0-9.]/g, ''));
                                            if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                                              return { min: minPrice, max: maxPrice };
                                            }
                                          }
                                        }
                                      }

                                      return null;
                                    })
                                    .filter(Boolean),
                                [filter.values]
                              );

                              if (priceValues.length === 0) {
                                return null;
                              }

                              // Store the original absolute price bounds in a ref to prevent them from changing
                              const originalPriceBoundsRef = useRef(null);

                              // Use passed original bounds if available, otherwise calculate from current data
                              if (!originalPriceBoundsRef.current) {
                                if (originalPriceBounds[filter.key]?.min !== undefined && originalPriceBounds[filter.key]?.max !== undefined) {
                                  // Use the passed original bounds (preferred method)
                                  originalPriceBoundsRef.current = {
                                    min: originalPriceBounds[filter.key].min,
                                    max: originalPriceBounds[filter.key].max
                                  };
                                } else if (priceValues.length > 0) {
                                  // Fallback: calculate from current price values (may be filtered)
                                  const allMinPrices = priceValues.map((p) => p.min);
                                  const allMaxPrices = priceValues.map((p) => p.max);
                                  
                                  originalPriceBoundsRef.current = {
                                    min: Math.min(...allMinPrices),
                                    max: Math.max(...allMaxPrices)
                                  };
                                }
                              }

                              // Use original bounds for the slider's min/max range
                              const absoluteMinPrice = originalPriceBoundsRef.current?.min ?? 0;
                              const absoluteMaxPrice = originalPriceBoundsRef.current?.max ?? 1000;

                              const currentPriceFilter = activeFilters[filter.key] || [];

                              const [initialMin, initialMax] = useMemo(() => {
                                let currentMin = absoluteMinPrice;
                                let currentMax = absoluteMaxPrice;
                                if (currentPriceFilter.length > 0) {
                                  const filterValue = currentPriceFilter[0];
                                  if (filterValue.includes('-')) {
                                    const [min, max] = filterValue.split('-').map(Number);
                                    if (!isNaN(min) && !isNaN(max)) {
                                      currentMin = min;
                                      currentMax = max;
                                    }
                                  } else {
                                    try {
                                      const parsed = JSON.parse(filterValue);
                                      if (parsed.price) {
                                        currentMin = parsed.price.min;
                                        currentMax = parsed.price.max;
                                      }
                                    } catch {}
                                  }
                                }
                                return [currentMin, currentMax];
                              }, [currentPriceFilter, absoluteMinPrice, absoluteMaxPrice]);

                              // Initialize price range from external state or use defaults
                              // If the price filter is removed, always use original bounds for currentPriceRange
                              const isFilterRemoved =
                                (!activeFilters[filter.key] || activeFilters[filter.key].length === 0);
                              const currentPriceRange = isFilterRemoved
                                ? [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]
                                : (priceRanges[filter.key] || [initialMin, initialMax]);

                              // Local state for input values to prevent focus loss during typing
                              const [localMinValue, setLocalMinValue] = useState(currentPriceRange[0]);
                              const [localMaxValue, setLocalMaxValue] = useState(currentPriceRange[1]);

                              // Update local values when currentPriceRange changes (from external sources like slider)
                              useEffect(() => {
                                // If the filter is removed and currentPriceRange matches original bounds, force reset
                                if (
                                  currentPriceRange[0] === originalPriceBoundsRef.current?.min &&
                                  currentPriceRange[1] === originalPriceBoundsRef.current?.max
                                ) {
                                  setLocalMinValue(originalPriceBoundsRef.current.min);
                                  setLocalMaxValue(originalPriceBoundsRef.current.max);
                                } else {
                                  setLocalMinValue(currentPriceRange[0]);
                                  setLocalMaxValue(currentPriceRange[1]);
                                }
                              }, [currentPriceRange]);

                              // Initialize external state only once when component mounts or when filter changes from URL
                              useEffect(() => {
                                if (!priceRanges[filter.key] && handleSetPriceRange) {
                                  handleSetPriceRange(filter.key, [initialMin, initialMax]);
                                }
                              }, [initialMin, initialMax, filter.key]);

                              const applyPriceFilter = useCallback((range) => {
                                // If range is not set or is the full range, remove the price filter
                                const isUnset =
                                  range == null ||
                                  range.length !== 2 ||
                                  range[0] === undefined || range[1] === undefined ||
                                  range[0] === '' || range[1] === '' ||
                                  (Number(range[0]) === Number(absoluteMinPrice) && Number(range[1]) === Number(absoluteMaxPrice));

                                const newFiltersData = { ...activeFilters };
                                delete newFiltersData[filter.key];
                                if (!isUnset) {
                                  newFiltersData[filter.key] = [`${Number(range[0])}-${Number(range[1])}`];
                                }

                                applyFilters(newFiltersData, isSearchToolbar ? 'search' : 'plp');
                              }, [activeFilters, filter.key, absoluteMinPrice, absoluteMaxPrice, applyFilters, isSearchToolbar]);

                              const handleInputChange = (index, value) => {
                                let numValue = Number(value);
                                if (isNaN(numValue)) numValue = index === 0 ? absoluteMinPrice : absoluteMaxPrice;
                                const newRange = [...currentPriceRange];
                                if (index === 0) newRange[0] = Math.min(numValue, currentPriceRange[1]);
                                else newRange[1] = Math.max(numValue, currentPriceRange[0]);
                                // If both min and max are at their absolute bounds, treat as unset
                                if (newRange[0] === absoluteMinPrice && newRange[1] === absoluteMaxPrice) {
                                  // Reset to original bounds, not last filtered values
                                  if (handleSetPriceRange) handleSetPriceRange(filter.key, [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
                                  setLocalMinValue(originalPriceBoundsRef.current.min);
                                  setLocalMaxValue(originalPriceBoundsRef.current.max);
                                  applyPriceFilter([originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
                                } else {
                                  if (handleSetPriceRange) handleSetPriceRange(filter.key, newRange);
                                  applyPriceFilter(newRange);
                                }
                              };

                              // Debounced version of handleInputChange to prevent immediate re-renders
                              const debouncedHandleInputChange = useDebounce(handleInputChange, 500);

                              // Handle local input changes without triggering filter updates immediately
                              const handleLocalInputChange = (index, value) => {
                                // Update local state immediately for responsive UI
                                let numValue = Number(value);
                                if (isNaN(numValue)) numValue = index === 0 ? absoluteMinPrice : absoluteMaxPrice;
                                if (index === 0) {
                                  setLocalMinValue(numValue);
                                } else {
                                  setLocalMaxValue(numValue);
                                }
                                // Debounce the actual filter update
                                debouncedHandleInputChange(index, value);
                              };

                              const handleSliderChange = useCallback(({ min, max }) => {
                                const newRange = [min, max];
                                // Only update if the range actually changed to prevent infinite loops
                                if (currentPriceRange[0] !== min || currentPriceRange[1] !== max) {
                                  // If slider is reset to full range, use original bounds
                                  if (min === absoluteMinPrice && max === absoluteMaxPrice) {
                                    if (handleSetPriceRange) handleSetPriceRange(filter.key, [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
                                    setLocalMinValue(originalPriceBoundsRef.current.min);
                                    setLocalMaxValue(originalPriceBoundsRef.current.max);
                                    applyPriceFilter([originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
                                  } else {
                                    if (handleSetPriceRange) handleSetPriceRange(filter.key, newRange);
                                    applyPriceFilter(newRange);
                                  }
                                }
                              }, [currentPriceRange, handleSetPriceRange, applyPriceFilter, filter.key, absoluteMinPrice, absoluteMaxPrice]);

                              return (
                                <div className='w-full space-y-4'>
                                  <div className='w-full mt-4 mb-2'>
                                    <MultiRangeSlider
                                      min={absoluteMinPrice}
                                      max={absoluteMaxPrice}
                                      initialMin={currentPriceRange[0]}
                                      initialMax={currentPriceRange[1]}
                                      onChange={({ min, max }) => handleSliderChange({ min, max })}
                                    />
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            // Options list (checkbox-style inputs)
                            filter.values?.map((option, optionIndex) => {
                              const filterValue = extractFilterValue(option);
                              const isActive = activeFilters[filterKey]?.includes(filterValue);

                              return (
                                <label key={optionIndex} className='flex items-center justify-between'>
                                  <div className='flex items-center'>
                                    {/* Hidden native input for accessibility, visual checkbox rendered with span */}
                                    <input
                                      type='checkbox'
                                      checked={!!isActive}
                                      onChange={() => handleMobileFilterSelect(filter, option)}
                                      className='sr-only'
                                      aria-checked={!!isActive}
                                    />

                                    <span
                                      className={`inline-flex items-center justify-center h-5 w-5 rounded-sm border flex-shrink-0 ${
                                        isActive ? 'bg-black border-black text-white' : 'bg-white border-[#bcbdbf]'
                                      }`}
                                      aria-hidden='true'
                                    >
                                      {isActive && (
                                        <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                          <path d='M20 6L9 17l-5-5' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
                                        </svg>
                                      )}
                                    </span>

                                    <span className='ml-3'>{option.label}</span>
                                  </div>

                                  {/* Right-side check icon for larger screens */}
                                  {isActive && <i className='ml-2 text-xs icon icon-check text-primary' />}
                                </label>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-start justify-center pt-6 w-[70px] border-l border-[#e5e5e6]">
              <button
                    onClick={() => closeMobileFilterPanel()}
                    className='p-2'
                  >
                    <i className='icon icon-close text-[20px]' />
              </button>
            </div>
          </div>
            <button
              onClick={() => {
                closeMobileFilterPanel();
              }}
              className='fixed bottom-0 w-full button button-primary'
            >
              View products
            </button>
        </div>
      </>
    );
  };

  // Full Width Filter Dropdown Component (for desktop individual filters layout)
  const FullWidthFilterDropdown = () => {
    if (!openFilterKey) return null;
    const currentFilter = filters.find((f: any) => f.key === openFilterKey) as any;
    if (!currentFilter) return null;

    // Close dropdown when clicking outside or scrolling
    useEffect(() => {
      const handleClickOutside = (event) => {
        // Don't close if clicking on filter buttons or the dropdown content
        if (event.target.closest('.individual-filter-dropdown') || 
            event.target.closest('.full-width-filter-dropdown')) {
          return;
        }
        setOpenFilterKey(null);
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Handle filter option selection
    const handleFilterSelect = (option) => {
      // Extract clean filter value using utility function
      const filterValue = extractFilterValue(option);

      const newFilters = { ...activeFilters };
      if (!newFilters[openFilterKey]) {
        newFilters[openFilterKey] = [];
      }

      // Toggle filter value
      if (newFilters[openFilterKey].includes(filterValue)) {
        newFilters[openFilterKey] = newFilters[openFilterKey].filter(v => v !== filterValue);
        if (newFilters[openFilterKey].length === 0) {
          delete newFilters[openFilterKey];
        }
      } else {
        newFilters[openFilterKey].push(filterValue);
      }

      applyFilters(newFilters, isSearchToolbar ? 'search' : 'plp');
    };

    // Handle price range changes
    const handlePriceChange = (minPrice, maxPrice) => {
      const newFilters = { ...activeFilters };
      
      if (minPrice !== null || maxPrice !== null) {
        // Format price range for Shopify
        let priceValue = '';
        if (minPrice !== null && maxPrice !== null) {
          priceValue = `${minPrice}-${maxPrice}`;
        } else if (minPrice !== null) {
          priceValue = `${minPrice}`;
        } else if (maxPrice !== null) {
          priceValue = `-${maxPrice}`;
        }
        
        newFilters[openFilterKey] = [priceValue];
      } else {
        // Clear price filter
        delete newFilters[openFilterKey];
      }

      applyFilters(newFilters, isSearchToolbar ? 'search' : 'plp');
    };

    // Check if this is a price filter
    const isPriceFilter = (
      currentFilter.key === 'Price' ||
      currentFilter.key === 'price' ||
      currentFilter.type === 'PRICE' ||
      currentFilter.type === 'PRICE_RANGE' ||
      currentFilter.key === 'filter.v.price' ||
      currentFilter.key?.includes('price') ||
      currentFilter.label?.toLowerCase().includes('price')
    );

    // Check if this is a color filter
    const isColorFilter = currentFilter.key?.includes('color') || currentFilter.key?.includes('colour');

    // Check if this is a size filter
    const isSizeFilter = currentFilter.type === 'SIZE' || currentFilter.key === 'options.size';

    // Helper function to detect if a value is a color
    const isColorValue = (value) => {
      if (!value) return false;
      const str = value.toString().toLowerCase();

      // Check for hex colors
      if (/^#[0-9a-f]{3,6}$/i.test(str)) return true;

      // Check for rgb/rgba
      if (/^rgba?\(/i.test(str)) return true;

      // Check for common color names
      const colorNames = [
        'red', 'blue', 'green', 'yellow', 'purple', 'pink', 'orange', 'brown', 
        'black', 'white', 'gray', 'grey', 'navy', 'beige', 'sage', 'burgundy', 
        'lavender', 'turquoise', 'coral', 'mint', 'cream', 'ivory', 'gold', 'silver'
      ];

      return colorNames.some((color) => str.includes(color));
    };

    // Helper function to convert color names to CSS values  
    const getColorCSSValue = (colorName) => {
      if (!colorName) return '#cccccc';
      const name = colorName.toLowerCase();

      // Return hex colors as-is
      if (/^#[0-9a-f]{3,6}$/i.test(name)) return name;
      if (/^rgba?\(/i.test(name)) return name;

      // Common color mappings
      const colorMap = {
        red: '#ff0000', blue: '#0000ff', green: '#008000', yellow: '#ffff00',
        purple: '#800080', pink: '#ffc0cb', orange: '#ffa500', brown: '#a52a2a',
        black: '#000000', white: '#ffffff', gray: '#808080', grey: '#808080',
        navy: '#000080', beige: '#f5f5dc', sage: '#9caf88', burgundy: '#800020'
      };

      for (const [key, value] of Object.entries(colorMap)) {
        if (name.includes(key)) return value;
      }

      return '#cccccc';
    };

    // Color swatch component
    const ColourSwatch = ({ option, isActive }: { option: any, isActive: boolean }) => {
      const colorValue = option.swatch?.color || getColorCSSValue(option.label);
      
      return (
        <button
          className={`flex items-center justify-between p-3 text-sm transition-colors`}
          onClick={() => handleFilterSelect(option)}
        >
          <div className='flex items-center space-x-3'>
            {/* Outer circle is 1.5rem (h-6 w-6) with 1px border; when active border turns black and padding increases to create gap */}
            <span
              className={`flex-shrink-0 rounded-full h-6 w-6 flex items-center justify-center border ${
                isActive ? 'border-black p-1' : 'border-[#bcbdbf]'
              }`}
              aria-hidden='true'
              style={{ boxSizing: 'border-box' }}
            >
              <span className='w-full h-full rounded-full'>
                <img
                  src={`${window?.filesURL}${option.label.toLowerCase()}.png`}
                  alt={option.label}
                  className='object-cover w-full h-full rounded-full'
                />
              </span>
            </span>

            <span>{option.label}</span>
          </div>
          {isActive && <i className='text-xs icon icon-check text-primary' />}
        </button>
      );
    };

    // Size button component
    const SizeButton = ({ option, isActive }: { option: any, isActive: boolean }) => (
      <button
        className={`flex items-center justify-center px-3 py-2 rounded border text-sm font-medium transition-colors ${
          isActive 
            ? 'border-primary bg-primary text-white'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
        onClick={() => handleFilterSelect(option)}
      >
        {option.label}
      </button>
    );

    // Price range component - EXACT COPY FROM COLLECTIONSIDEBAR
    const PriceRangeFilter = () => {
      // Extract min and max prices from filter values - memoized to prevent infinite re-renders
      const priceValues = useMemo(
        () =>
          currentFilter.values
            .map((value) => {
              // First try to use the input field (already parsed object)
              if (value.input) {
                // Handle string input that needs parsing
                if (typeof value.input === 'string') {
                  try {
                    const parsedInput = JSON.parse(value.input);
                    if (parsedInput.price) {
                      return { min: parsedInput.price.min, max: parsedInput.price.max };
                    }
                  } catch (e) {
                    // Silent fallback
                  }
                }
                // Handle already parsed object
                else if (typeof value.input === 'object' && value.input.price) {
                  return { min: value.input.price.min, max: value.input.price.max };
                }
              }

              // Fallback to parsing the key field
              try {
                const parsed = JSON.parse(value.input);
                if (parsed.price) {
                  return { min: parsed.price.min, max: parsed.price.max };
                }
              } catch (e) {
                // Handle non-JSON price values
                const parts = value.input.split('-');
                if (parts.length === 2) {
                  const minPrice = parseFloat(parts[0]);
                  const maxPrice = parseFloat(parts[1]);
                  if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                    return { min: minPrice, max: maxPrice };
                  }
                }

                // Try to extract from label or other formats
                if (value.label && value.label !== 'Price') {
                  const labelParts = value.label.split('-');
                  if (labelParts.length === 2) {
                    const minPrice = parseFloat(labelParts[0].replace(/[^0-9.]/g, ''));
                    const maxPrice = parseFloat(labelParts[1].replace(/[^0-9.]/g, ''));
                    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
                      return { min: minPrice, max: maxPrice };
                    }
                  }
                }
              }

              return null;
            })
            .filter(Boolean),
        [currentFilter.values]
      );

      if (priceValues.length === 0) {
        return null;
      }

      // Store the original absolute price bounds in a ref to prevent them from changing
      const originalPriceBoundsRef = useRef(null);

      // Use passed original bounds if available, otherwise calculate from current data
      if (!originalPriceBoundsRef.current) {
        if (originalPriceBounds[currentFilter.key]?.min !== undefined && originalPriceBounds[currentFilter.key]?.max !== undefined) {
          // Use the passed original bounds (preferred method)
          originalPriceBoundsRef.current = {
            min: originalPriceBounds[currentFilter.key].min,
            max: originalPriceBounds[currentFilter.key].max
          };
        } else if (priceValues.length > 0) {
          // Fallback: calculate from current price values (may be filtered)
          const allMinPrices = priceValues.map((p) => p.min);
          const allMaxPrices = priceValues.map((p) => p.max);
          
          originalPriceBoundsRef.current = {
            min: Math.min(...allMinPrices),
            max: Math.max(...allMaxPrices)
          };
        }
      }

      // Use original bounds for the slider's min/max range
      const absoluteMinPrice = originalPriceBoundsRef.current?.min ?? 0;
      const absoluteMaxPrice = originalPriceBoundsRef.current?.max ?? 1000;

      const currentPriceFilter = activeFilters[currentFilter.key] || [];

      const [initialMin, initialMax] = useMemo(() => {
        let currentMin = absoluteMinPrice;
        let currentMax = absoluteMaxPrice;
        if (currentPriceFilter.length > 0) {
          const filterValue = currentPriceFilter[0];
          if (filterValue.includes('-')) {
            const [min, max] = filterValue.split('-').map(Number);
            if (!isNaN(min) && !isNaN(max)) {
              currentMin = min;
              currentMax = max;
            }
          } else {
            try {
              const parsed = JSON.parse(filterValue);
              if (parsed.price) {
                currentMin = parsed.price.min;
                currentMax = parsed.price.max;
              }
            } catch {}
          }
        }
        return [currentMin, currentMax];
      }, [currentPriceFilter, absoluteMinPrice, absoluteMaxPrice]);

      // Initialize price range from external state or use defaults
      // If the price filter is removed, always use original bounds for currentPriceRange
      const isFilterRemoved =
        (!activeFilters[currentFilter.key] || activeFilters[currentFilter.key].length === 0);
      const currentPriceRange = isFilterRemoved
        ? [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]
        : (priceRanges[currentFilter.key] || [initialMin, initialMax]);

      // Local state for input values to prevent focus loss during typing
      const [localMinValue, setLocalMinValue] = useState(currentPriceRange[0]);
      const [localMaxValue, setLocalMaxValue] = useState(currentPriceRange[1]);

      // Update local values when currentPriceRange changes (from external sources like slider)
      useEffect(() => {
        // If the filter is removed and currentPriceRange matches original bounds, force reset
        if (
          currentPriceRange[0] === originalPriceBoundsRef.current?.min &&
          currentPriceRange[1] === originalPriceBoundsRef.current?.max
        ) {
          setLocalMinValue(originalPriceBoundsRef.current.min);
          setLocalMaxValue(originalPriceBoundsRef.current.max);
        } else {
          setLocalMinValue(currentPriceRange[0]);
          setLocalMaxValue(currentPriceRange[1]);
        }
      }, [currentPriceRange]);

      // Initialize external state only once when component mounts or when filter changes from URL
      useEffect(() => {
        if (!priceRanges[currentFilter.key] && handleSetPriceRange) {
          handleSetPriceRange(currentFilter.key, [initialMin, initialMax]);
        }
      }, [initialMin, initialMax, currentFilter.key]);

      const applyPriceFilter = useCallback((range) => {
        // If range is not set or is the full range, remove the price filter
        const isUnset =
          range == null ||
          range.length !== 2 ||
          range[0] === undefined || range[1] === undefined ||
          range[0] === '' || range[1] === '' ||
          (Number(range[0]) === Number(absoluteMinPrice) && Number(range[1]) === Number(absoluteMaxPrice));

        const newFiltersData = { ...activeFilters };
        delete newFiltersData[currentFilter.key];
        if (!isUnset) {
          newFiltersData[currentFilter.key] = [`${Number(range[0])}-${Number(range[1])}`];
        }

        applyFilters(newFiltersData, isSearchToolbar ? 'search' : 'plp');
      }, [activeFilters, currentFilter.key, absoluteMinPrice, absoluteMaxPrice, applyFilters, isSearchToolbar]);

      const handleMinChange = (value) => {
        let newMin = Number(value);
        if (isNaN(newMin) || newMin < absoluteMinPrice) newMin = absoluteMinPrice;
        newMin = Math.min(newMin, currentPriceRange[1]);
        const newRange = [newMin, currentPriceRange[1]];
        if (handleSetPriceRange) {
          handleSetPriceRange(currentFilter.key, newRange);
        }
        applyPriceFilter(newRange);
      };

      const handleMaxChange = (value) => {
        let newMax = Number(value);
        if (isNaN(newMax) || newMax > absoluteMaxPrice) newMax = absoluteMaxPrice;
        newMax = Math.max(newMax, currentPriceRange[0]);
        const newRange = [currentPriceRange[0], newMax];
        if (handleSetPriceRange) {
          handleSetPriceRange(currentFilter.key, newRange);
        }
        applyPriceFilter(newRange);
      };

      const handleInputChange = (index, value) => {
        let numValue = Number(value);
        if (isNaN(numValue)) numValue = index === 0 ? absoluteMinPrice : absoluteMaxPrice;
        const newRange = [...currentPriceRange];
        if (index === 0) newRange[0] = Math.min(numValue, currentPriceRange[1]);
        else newRange[1] = Math.max(numValue, currentPriceRange[0]);
        // If both min and max are at their absolute bounds, treat as unset
        if (newRange[0] === absoluteMinPrice && newRange[1] === absoluteMaxPrice) {
          // Reset to original bounds, not last filtered values
          if (handleSetPriceRange) handleSetPriceRange(currentFilter.key, [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
          setLocalMinValue(originalPriceBoundsRef.current.min);
          setLocalMaxValue(originalPriceBoundsRef.current.max);
          applyPriceFilter([originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
        } else {
          if (handleSetPriceRange) handleSetPriceRange(currentFilter.key, newRange);
          applyPriceFilter(newRange);
        }
      };

      // Debounced version of handleInputChange to prevent immediate re-renders
      const debouncedHandleInputChange = useDebounce(handleInputChange, 500);

      // Handle local input changes without triggering filter updates immediately
      const handleLocalInputChange = (index, value) => {
        // Update local state immediately for responsive UI
        let numValue = Number(value);
        if (isNaN(numValue)) numValue = index === 0 ? absoluteMinPrice : absoluteMaxPrice;
        if (index === 0) {
          setLocalMinValue(numValue);
        } else {
          setLocalMaxValue(numValue);
        }
        // Debounce the actual filter update
        debouncedHandleInputChange(index, value);
      };

      const handleSliderChange = useCallback(({ min, max }) => {
        const newRange = [min, max];
        // Only update if the range actually changed to prevent infinite loops
        if (currentPriceRange[0] !== min || currentPriceRange[1] !== max) {
          // If slider is reset to full range, use original bounds
          if (min === absoluteMinPrice && max === absoluteMaxPrice) {
            if (handleSetPriceRange) handleSetPriceRange(currentFilter.key, [originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
            setLocalMinValue(originalPriceBoundsRef.current.min);
            setLocalMaxValue(originalPriceBoundsRef.current.max);
            applyPriceFilter([originalPriceBoundsRef.current.min, originalPriceBoundsRef.current.max]);
          } else {
            if (handleSetPriceRange) handleSetPriceRange(currentFilter.key, newRange);
            applyPriceFilter(newRange);
          }
        }
      }, [currentPriceRange, handleSetPriceRange, applyPriceFilter, currentFilter.key, absoluteMinPrice, absoluteMaxPrice]);

      return (
        <div className='w-full h-full'>
          <div className='flex items-center justify-between mt-4 mb-2'>
            <MultiRangeSlider
              min={absoluteMinPrice}
              max={absoluteMaxPrice}
              initialMin={currentPriceRange[0]}
              initialMax={currentPriceRange[1]}
              onChange={({ min, max }) => handleSliderChange({ min, max })}
            />
          </div>
        </div>
      );
    };

    return (
      <div className='w-full duration-200 bg-white full-width-filter-dropdown animate-in slide-in-from-top-2'>
        <div className='pt-10 pb-4 min-h-[140px] border-t border-[#e5e5e6]'>
          <div className='absolute top-[80px] right-5'>
            <button
              onClick={() => setOpenFilterKey(null)}
              className='p-1 rounded hover:bg-gray-100'
            >
              <i className='text-sm text-gray-500 icon icon-close' />
            </button>
          </div>
          
          {isPriceFilter ? (
            <div className='max-w-md mx-auto'>
              <PriceRangeFilter />
            </div>
          ) : (
            <div className={`grid gap-3 grid-cols-2 md:grid-cols-4 md:px-[30px] lg:px-[60px]`}>
              {currentFilter.values?.map((option, index) => {
                const filterValue = extractFilterValue(option);
                const isActive = activeFilters[openFilterKey]?.includes(filterValue);
                
                if (isColorFilter) {
                  return (
                    <ColourSwatch
                      option={option}
                      isActive={isActive}
                    />
                  );
                } else if (isSizeFilter) {
                  return (
                    <SizeButton
                      option={option}
                      isActive={isActive}
                    />
                  );
                } else {
                  // Non-color, non-size options: render as a labeled checkbox square (1.5rem = h-6 w-6)
                  return (
                    <label
                      key={index}
                      className={`flex items-center justify-between text-sm transition-colors`}
                    >
                      <div className='flex items-center'>
                        <input
                          type='checkbox'
                          checked={!!isActive}
                          onChange={() => handleFilterSelect(option)}
                          className='sr-only'
                          aria-checked={!!isActive}
                        />

                        <span
                          className={`inline-flex items-center justify-center h-6 w-6 rounded-sm border flex-shrink-0 ${
                            isActive ? 'bg-black border-black text-white' : 'bg-white border-gray-300'
                          }`}
                          aria-hidden='true'
                        >
                          {isActive && (
                            <svg className='w-3 h-3' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                              <path d='M20 6L9 17l-5-5' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
                            </svg>
                          )}
                        </span>

                        <span className='ml-3'>{option.label}</span>
                      </div>

                      {/* Right-side check (for clarity on larger screens) */}
                      {isActive && <i className='ml-2 text-xs icon icon-check text-primary' />}
                    </label>
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render different layouts based on toolbarLayout setting
  const renderToolbarLayout = () => {
    switch (toolbarLayout) {
      case 'individual_filters':
        return (
          <div className='w-full'>
            {/* Main toolbar row */}
            <div className={`flex items-center ${isMobile ? '' : 'justify-between'}`}>
              {/* Left side - Mobile filter button (only on mobile) */}
              <div className={`flex items-center ${isMobile ? 'flex-1 px-8 py-4' : ''}`}>
                {isMobile ? (
                  <button
                    className={`${filterButtonClasses} flex w-full items-center justify-between`}
                    onClick={() => openMobileFilterPanel()}
                  >
                    <span>{filterLabel}</span>
                    <i className={`icon ${filterIconClass}`} style={{ fontSize: `${filterIconSize}px` }} />
                    {/* {activeFilterCount > 0 && (
                      <span className='flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full bg-primary'>
                        {activeFilterCount}
                      </span>
                    )} */}
                  </button>
                ) : (
                  // Desktop - Individual filter dropdowns
                  <div className='flex items-center space-x-8'>
                    {filters.map((filter: any, index: number) => {
                      const filterKey = filter.key;
                      return (
                        <IndividualFilterDropdown
                          filter={filter}
                          filterKey={filterKey}
                          loading={loading}
                        />
                      );
                   })}
                  </div>
                )}
                {productCountPosition === 'left' && <ProductCount />}
              </div>

              {showMobileDivider && isMobile && <div className='border-l border-[#e5e5e6] h-[56px]' />}

              {/* Center - Product count if positioned center */}
              {productCountPosition === 'center' &&
                <div className='flex items-center justify-center'>
                  <ProductCount />
                </div>
              }

              {/* Right side - Sort dropdown */}
              <div className={`flex items-center ${isMobile ? 'flex-1 px-8 py-4' : ''}`}>
                {productCountPosition === 'right' && <ProductCount />}
                <SortDropdown />
              </div>
            </div>

            {/* Mobile filter panel */}
            <MobileFilterPanel />
          </div>
        );

      case 'left_aligned':
        return (
          <div className='flex items-center justify-between w-full'>
            {/* Left side - Product count */}
            <div className='flex items-center'>{productCountPosition === 'left' && <ProductCount />}</div>

            {/* Center - Product count if positioned center */}
            <div className='flex items-center justify-center'>
              {productCountPosition === 'center' && <ProductCount />}
            </div>

            {/* Right side - Filter button and sort dropdown */}
            <div className='flex items-center md:space-x-4'>
              <SortDropdown />
              <FilterButton />
              {productCountPosition === 'right' && <ProductCount />}
            </div>
          </div>
        );

      case 'compact':
        return (
          <div className='flex items-center justify-between py-3 px-[14px] md:px-5 w-full'>
            {/* Left side - Product count if positioned left */}
            <div className='flex items-center'>{productCountPosition === 'left' && <ProductCount />}</div>

            {/* Center - Product count if positioned center */}
            <div className='flex items-center justify-center'>
              {productCountPosition === 'center' && <ProductCount />}
            </div>

            {/* Right side - Filter and sort together */}
            <div className='flex items-center space-x-4'>
              <FilterButton />
              <SortDropdown />
              {productCountPosition === 'right' && <ProductCount />}
            </div>
          </div>
        );

      default: // 'default'
        return (
          <div className='flex items-center justify-between py-3 px-[14px] md:px-5 w-full'>
            {/* Left side - Filter button */}
            <div className='flex items-center'>
              <FilterButton />
              {productCountPosition === 'left' && <ProductCount />}
            </div>

            {/* Center - Product count */}
            <div className='absolute flex items-center justify-center -translate-x-1/2 left-1/2'>
              {productCountPosition === 'center' && <ProductCount />}
            </div>

            {/* Right side - Sort dropdown */}
            <div className='flex items-center'>
              {productCountPosition === 'right' && <ProductCount />}
              <SortDropdown />
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div
        className={`collection-toolbar z-[1] w-full bg-white sticky top-0 mb-1 lg:mb-4 transition-all duration-300 ${
          type === 'plp' ? 'md:py-4' : ''
        }`}
      >
        <div>{renderToolbarLayout()}</div>
        {/* Active filters displayed under the toolbar - only show for non-individual-filters layout */}
        {toolbarLayout !== 'individual_filters' && showActiveFiltersInToolbar && <ActiveFiltersBar />}
      </div>

      {/* Full-width filter dropdown for individual_filters layout - renders outside sticky container to push content down */}
      {toolbarLayout === 'individual_filters' && !isMobile && openFilterKey && (
        <div className='px-4 md:px-[30px] lg:px-[60px] w-full'>
          <FullWidthFilterDropdown />
        </div>
      )}

      {toolbarLayout === 'individual_filters' && showActiveFiltersInToolbar && <ActiveFiltersBar />}

      {/* Mobile filter panel - only render for individual_filters layout */}
      {toolbarLayout === 'individual_filters' && <MobileFilterPanel />}
    </>
  );
};
